/**
 * Saving bytes a raw `fetch` brought back, without saving them for someone who
 * has left.
 *
 * The session guard (stores/sessionGuard.ts) is a Feathers hook: it ends every
 * SERVICE call a sign-out overtakes, but a raw `fetch` (an authenticated
 * download or upload), `response.blob()`, a dynamic `import()` or the clipboard
 * never passes through it. Such a call settles normally after the sign-out —
 * with the bearer token read before it — and the code behind the await carries
 * on: it clicks `<a download>` and the previous person's invoice or client
 * document lands on disk in the next person's session, or it writes an upload
 * notice and starts a reload there.
 *
 * So every store method that does raw transfer work takes
 * `const ended = sessionMark(this.rootStore)` at entry and checks `ended()`
 * after every await, in its catch, and before the state writes in its finally
 * (`rawTransferSessionMark.test.ts` fails a store method that calls `fetch(` or
 * `.blob()` without one). {@link saveBlobInSession} is the last of those checks,
 * at the point of no return, so no download path can skip it.
 */

/**
 * How long an object URL outlives its click. `click()` only STARTS a
 * download: a browser that reads the blob URL after this tick (Safari, older
 * Firefox) finds a synchronously revoked one gone and fails the download.
 */
export const OBJECT_URL_REVOKE_DELAY_MS = 30_000;

/**
 * Hand `blob` to the browser as a file named `fileName` (a transient object
 * URL and an `<a download>` click) — unless the session that asked for it has
 * ended, in which case nothing is created or clicked and the answer is false.
 */
export function saveBlobInSession(ended: () => boolean, blob: Blob, fileName: string): boolean {
  if (ended()) return false;
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    try {
      anchor.click();
    } finally {
      // A click that throws (a browser or extension refusing the download)
      // must not leave an invisible anchor in the page.
      anchor.remove();
    }
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_REVOKE_DELAY_MS);
  }
  return true;
}

/** The `Authorization` header for a raw request made with `token`, or none. */
export function bearerHeaders(token: string | null | undefined): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {};
}
